import { ComponentBase } from "../core/ComponentBase.js";
export class Dashboard extends ComponentBase {
    static styles = `
    /* Main container and header */
    .dashboard-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      background-color: var(--bs-body-bg); /* Use Bootstrap's body background */
      color: var(--bs-body-color);      /* Use Bootstrap's body text color */
    }

    /* Header area containing tabs and controls */
    .dashboard-container > .d-flex.align-items-center {
        padding: 0 16px; /* Align with content padding */
        border-bottom: 1px solid var(--bs-border-color);
        background-color: rgba(var(--bs-body-color-rgb), 0.03); /* Slight distinction for the header area */
        flex-wrap: wrap; /* Ensure it wraps cleanly on smaller viewports */
        gap: 8px;
    }

    .dashboard-tabs {
      overflow: hidden;
      white-space: nowrap;
      margin-bottom: 0px;
      display: flex;
      flex-wrap: nowrap;
      border-bottom: none; /* Removed because parent has it */
      padding-top: 8px;
    }
    .dashboard-tabs:hover {
      overflow-x: auto;
      scrollbar-width: thin;
    }
    .dashboard-tabs .nav-item {
      flex: 0 0 auto;
    }

    .dashboard-new-tab-button {
      margin-left: 0.5rem;
      margin-top: 8px;
      border: 1px dashed var(--bs-border-color);
    }

    .nav-link {
        color: var(--bs-link-color, #0056b3) !important;
    }

    .nav-link:hover,
    .nav-link:focus {
      color: var(--bs-link-hover-color, #0056b3) !important;
    }
    .nav-tabs .nav-link.active {
      border-color: transparent transparent var(--bs-body-bg, #fff);
      background-color: var(--bs-body-bg);
      color: var(--bs-body-color) !important;
    }

    .dashboard-tab-close {
      margin-left: 0.5rem;
      color: var(--bs-danger, #dc3545);
      font-weight: var(--ab-weight-bold);
      cursor: pointer;
      border: none;
      background: transparent;
      font-size: var(--ab-text-sm);
      transition: color var(--ab-t-fast), box-shadow var(--ab-t-fast);
      border-radius: var(--ab-radius-xs);
    }
    .dashboard-tab-close:hover {
      color: var(--bs-danger-text-emphasis, #a71d2a);
    }
    .dashboard-tab-close:focus-visible {
      outline: none;
      box-shadow: var(--ab-focus-ring);
    }
    /* Grid for widget layout – each cell is 10px in height, and columns have a min of 10px */
    .dashboard-content-resizable {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(10px, 1fr));
      gap: 10px;
      grid-auto-rows: 10px;
      grid-auto-flow: dense;
      overflow-y: auto;
      scrollbar-width: thin;
      flex-grow: 1;
      min-height: 0;
      padding: 10px;
      box-sizing: border-box;
    }

    .dashboard-content-auto {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start; /* Or space-between, etc. */
        gap: 16px;
        overflow-y: auto;
        scrollbar-width:thin;
        flex-grow: 1;
        min-height: 0;
        padding: 16px;
        box-sizing: border-box;
    }

    .control-bar {
      display: flex;
      align-items: center;
      padding-top: 8px;
      padding-right: 0; /* Reset since parent has padding */
    }
    .control-bar > * {
      margin-left: 0.5rem;
    }

    .drop-highlight {
      outline: 2px dashed var(--bs-primary, red); /* Use Bootstrap's primary color */
      outline-offset: -2px;
    }

    .tab-content {
        margin-top: 5px;
        flex-grow: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }

    .tab-pane {
        flex-grow: 1;
        min-height: 0;
        display: none;
        flex-direction: column;
    }

    .tab-pane.active {
        display: flex;
    }


    @media (max-width: 768px) {
      .dashboard-container > .d-flex.align-items-center {
          padding: 8px 12px !important;
          gap: 6px;
      }

      .dashboard-tabs {
          padding-top: 4px;
          flex-grow: 1;
      }

      .dashboard-new-tab-button {
          margin-top: 4px;
      }

      .control-bar {
          padding-top: 4px;
          margin-left: auto !important;
          flex-wrap: wrap;
          gap: 4px;
      }

      .control-bar > * {
          margin-left: 0.25rem;
      }

      .dashboard-content-resizable {
          /* Single column, rows expand to their content height */
          grid-template-columns: 1fr !important;
          grid-auto-rows: auto !important;
          padding: 6px;
          gap: 12px;
      }
      /* Reset span overrides so each widget takes only the space it needs */
      .dashboard-content-resizable > * {
          grid-column: 1 / -1 !important;
          grid-row: auto !important;
          min-height: min(360px, 85vw) !important; /* Make it look square on mobile screens */
      }
      .dashboard-content-auto {
          padding: 6px;
          gap: 12px;
      }
      .dashboard-content-auto > * {
          width: 100% !important;
          max-width: 100% !important;
          min-height: min(360px, 85vw) !important; /* Make it look square on mobile screens */
      }

      .widget-container-resizable {
          grid-column: 1 / -1 !important;
          grid-row: auto !important;
      }
    }
`;

    // Replace your existing getDefaultConfig() with this:
    static getDefaultConfig() {
        return {
            instanceId: 'defaultDashboard',
            // new master switch:
            disableConfiguration: false,
            // per-feature switches (now overridden by disableConfiguration):
            disableCustomizations: false,
            disableAddWidget: false,
            disableTabButtons: false,
            resizableWidgets: true,
        };
    }

    constructor(config) {
        super(config);

        // Merge in our defaults and then user-provided config
        this.config = {
            ...Dashboard.getDefaultConfig(),
            ...this.config
        };

        // If the master switch is on, turn off every UI customization flag
        if (this.config.disableConfiguration) {
            this.config.disableCustomizations = true;
            this.config.disableAddWidget = true;
            this.config.disableTabButtons = true;
        }

        // rest of original constructor unchanged
        this.tabs = [];    // each tab: { id, title, active, widgets: [...] }
        this.activeTabId = null;
        this.nextWidgetId = 1;
        this.nextTabId = 1;
        this.tabNavElements = {};    // tabId -> <li>
        this.tabContentElements = {};    // tabId -> pane
        this.widgetInstances = {};    // widgetId -> component instance
        this.dashboardConfig = null;  // loaded from RPC
        this.availableWidgets = [];    // from RPC
        this.container = null;
        this.containerId = `dashboard-container-${config.instanceId}`;
        // date‐range persistence
        this.currentDateRange = this.loadSavedDateRange() || this.getDefaultDateRange();
        this.resizeObserver = null;

        // --- FIX: Add a flag to track if the layout is statically configured ---
        this.isStaticallyConfigured = false;

        this.applyStyles();
    }

    async initialize() {
        await this.render();
        const contentArea = this.container.querySelector('.dashboard-content-resizable, .dashboard-content-auto');
        if (contentArea) {
            this.resizeObserver = new ResizeObserver(this.debounce(() => {
                Object.values(this.widgetInstances).forEach(widget => {
                    if (widget && typeof widget.resize === 'function') {
                        widget.resize();
                    }
                });
            }, 200)); // Debounce to avoid excessive calls
            this.resizeObserver.observe(contentArea);
        }
        await super.initialize();
    }


    migrateWidgets(widgets) {
        if (!widgets) return widgets;
        const cellOld = 25;
        const gapOld = 16;
        const cellNew = 10;
        const gapNew = 10;

        return widgets.map(w => {
            if (w._migrated) return w;
            const colsOld = w.cols !== undefined ? w.cols : 10;
            const rowsOld = w.rows !== undefined ? w.rows : 10;

            const wOld = colsOld * cellOld + (colsOld - 1) * gapOld;
            const hOld = rowsOld * cellOld + (rowsOld - 1) * gapOld;

            const colsNew = Math.max(1, Math.round((wOld + gapNew) / (cellNew + gapNew)));
            const rowsNew = Math.max(1, Math.round((hOld + gapNew) / (cellNew + gapNew)));

            return {
                ...w,
                cols: colsNew,
                rows: rowsNew,
                _migrated: true
            };
        });
    }

    async setDashboardLayout(widgetLayout, force = true) {
        // --- FIX: Set the flag to true when this method is called ---
        this.isStaticallyConfigured = true;

        // (A) Fetch the catalog so we can actually instantiate your widgets
        try {
            this.availableWidgets = await this.appClient.rpcClient.call(
                'DashboardRpc.getAvailableWidgets'
            );
        } catch (err) {
            this.log.error("Failed to load available widgets:", err);
            this.availableWidgets = [];
        }

        // (B) Ensure the DOM scaffold exists
        if (!this.container) {
            await this.render();
        }

        const migratedLayout = this.migrateWidgets(widgetLayout);

        // (C) Build a single “static” tab from your incoming layout
        const tabTitle = this.t('Dashboard');
        this.tabs = [{
            id: `tab-static-1`,
            title: tabTitle,
            active: true,
            widgets: migratedLayout.map(w => ({
                ...w,
                widgetId: w.widgetId || `${w.name}-${this.nextWidgetId++}`
            }))
        }];
        this.activeTabId = this.tabs[0].id;

        // (D) Now actually create all the DOM: tabs + widgets
        await this.reRenderAll();
    }


    async reRenderAll() {
        if (!this.container) return;

        // Rebuild the header (tabs and controls)
        this.rebuildHeader();

        // Clear content and widget instances
        this.tabContentContainer.innerHTML = '';
        this.widgetInstances = {};

        // Re-render all tabs and their widgets from the current state
        for (const tabData of this.tabs) {
            await this._renderTab(tabData); // Re-creates the tab and its widget-holder div
            await this._renderWidgetsForTab(tabData); // Populates the widget-holder
        }

        this.setActiveTab(this.activeTabId);
    }


    async initAfterLogin() {
        // Wire up server→client reloads for hot module replacement in dev mode
        this.appClient.events.server.on("reloadWidget", async (eventData) => {
            if (eventData.type === 'widget') {
                await this.reloadWidget(eventData);
            }
        });

        this.setDateRange(this.currentDateRange);

        if (!this.config.disableCustomizations && !this.isStaticallyConfigured) {
            try {
                this.availableWidgets = await this.appClient.rpcClient.call(
                    'DashboardRpc.getAvailableWidgets'
                );
                await this.loadDashboardConfig();
            } catch (err) {
                this.log.error("Failed to load available widgets:", err);
                this.availableWidgets = [];
                await this.loadDashboardConfig();
            }
        }
    }




    async render() {
        // This method is now only responsible for creating the initial DOM structure ONCE.
        if (!this.container) {
            this.container = document.createElement("div");
            this.container.id = this.containerId;
            this.container.className = "dashboard-container";

            this.header = document.createElement("div");
            this.header.className = "d-flex align-items-center";
            this.container.appendChild(this.header);

            this.tabContentContainer = document.createElement("div");
            this.tabContentContainer.className = "tab-content";
            this.container.appendChild(this.tabContentContainer);

            this.rebuildHeader(); // Build the header content
        }
        return this.container;
    }

    rebuildHeader() {
        this.header.innerHTML = ''; // Clear header completely

        this.tabsContainer = document.createElement("ul");
        this.tabsContainer.className = "nav nav-underline dashboard-tabs";
        this.tabsContainer.setAttribute("role", "tablist");
        this.header.appendChild(this.tabsContainer);

        if (!this.config.disableCustomizations) {
            this.newTabButton = document.createElement("button");
            this.newTabButton.className = "ab-icon-btn ab-icon-btn-sm dashboard-new-tab-button";
            this.newTabButton.title = this.t("Add New Tab");
            this.newTabButton.setAttribute("aria-label", this.t("Add New Tab"));
            this.newTabButton.innerHTML = `<i class="bi bi-plus-lg"></i>`;
            this.newTabButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.addTab();
            });
            this.header.appendChild(this.newTabButton);
        }

        this.controlBar = document.createElement("div");
        this.controlBar.className = "control-bar ms-auto";
        if (!this.config.disableAddWidget && !this.config.disableCustomizations) {
            const addWidgetButton = document.createElement("button");
            addWidgetButton.className = "ab-icon-btn text-primary border border-primary-subtle";
            addWidgetButton.title = this.t("Add Widget");
            addWidgetButton.setAttribute("aria-label", this.t("Add Widget"));
            addWidgetButton.innerHTML = `<i class="bi bi-plus-lg"></i>`;
            addWidgetButton.addEventListener('click', () => this.showWidgetModal());
            this.controlBar.appendChild(addWidgetButton);
        }
        const dateRangePicker = this.createDateRangePicker();
        this.controlBar.appendChild(dateRangePicker);
        this.header.appendChild(this.controlBar);

        this.appClient.tx.applyTranslations(this.header);
    }


    async _renderTab(tabData) {
        // Create tab navigation element.
        const li = document.createElement("li");
        li.className = "nav-item dashboard-tab";
        li.setAttribute("role", "presentation");
        const a = document.createElement("a");
        a.className = "nav-link";
        a.id = `${tabData.id}-tab`;
        a.href = `#${tabData.id}`;
        a.setAttribute("role", "tab");
        a.setAttribute("aria-controls", tabData.id);
        a.setAttribute("aria-selected", "false");

        const titleSpan = document.createElement("span");
        titleSpan.textContent = tabData.title;
        a.appendChild(titleSpan);

        if (!this.config.disableCustomizations) { // Only add close button if customizable
            const closeBtn = document.createElement("button");
            closeBtn.className = "dashboard-tab-close";
            closeBtn.innerHTML = `<i class="bi bi-x-lg"></i>`;
            closeBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.removeTab(tabData.id);
            });
            a.appendChild(closeBtn);
        }

        a.addEventListener('click', () => this.setActiveTab(tabData.id));

        if (!this.config.disableCustomizations) { // Only allow editing if customizable
            a.addEventListener('dblclick', () => {
                const newTitle = prompt("Enter new tab name:", tabData.title);
                if (newTitle && newTitle.trim() !== "") {
                    tabData.title = newTitle;
                    titleSpan.textContent = newTitle;
                    this.saveDashboardConfig();
                }
            });
        }

        li.appendChild(a);
        this.tabsContainer.appendChild(li);
        this.tabNavElements[tabData.id] = li;

        // Create tab content element.
        const tabContent = document.createElement("div");
        tabContent.className = "tab-pane fade";
        if (tabData.active) {
            tabContent.classList.add("show", "active");
        }
        tabContent.id = tabData.id;
        tabContent.setAttribute("role", "tabpanel");
        tabContent.setAttribute("aria-labelledby", `${tabData.id}-tab`);

        const widgetHolder = document.createElement("div");
        widgetHolder.className = this.config.resizableWidgets ? "dashboard-content-resizable" : "dashboard-content-auto";

        this.setupDragAndDrop(widgetHolder, tabData.id);

        tabContent.appendChild(widgetHolder);
        this.tabContentElements[tabData.id] = tabContent;
        this.tabContentContainer.appendChild(tabContent);
    }

    setupDragAndDrop(widgetHolder, tabId) {
        widgetHolder.addEventListener('dragover', (e) => {
            e.preventDefault();
            Array.from(widgetHolder.children).forEach(child => {
                child.classList.remove('drop-highlight');
            });

            // Exclude the widget being dragged from candidates
            const candidates = Array.from(widgetHolder.children).filter(
                child => !child.classList.contains('dragging')
            );
            if (candidates.length === 0) return;

            // Find the candidate closest to the pointer
            let targetChild = null;
            let minDistance = Infinity;
            const pointerX = e.clientX;
            const pointerY = e.clientY;

            candidates.forEach((child) => {
                const rect = child.getBoundingClientRect();
                const centerX = (rect.left + rect.right) / 2;
                const centerY = (rect.top + rect.bottom) / 2;
                const distance = Math.sqrt(
                    Math.pow(pointerX - centerX, 2) +
                    Math.pow(pointerY - centerY, 2)
                );
                if (distance < minDistance) {
                    minDistance = distance;
                    targetChild = child;
                }
            });

            if (targetChild) {
                targetChild.classList.add('drop-highlight');
                widgetHolder.dataset.dropTargetId = targetChild.dataset.instanceId;
            } else {
                widgetHolder.dataset.dropTargetId = '';
            }
        });

        widgetHolder.addEventListener('dragleave', (e) => {
            if (!widgetHolder.contains(e.relatedTarget)) {
                Array.from(widgetHolder.children).forEach(child => child.classList.remove('drop-highlight'));
                widgetHolder.dataset.dropTargetId = '';
            }
        });

        widgetHolder.addEventListener('drop', async (e) => {
            e.preventDefault();
            Array.from(widgetHolder.children).forEach(child => child.classList.remove('drop-highlight'));

            const dropTargetId = widgetHolder.dataset.dropTargetId;
            const sourceId = e.dataTransfer.getData('text/plain');

            if (!sourceId) return;

            if (dropTargetId && dropTargetId !== sourceId) {
                // Highlighted target exists — swap the two widgets
                await this.swapWidgets(sourceId, dropTargetId);
            } else if (!dropTargetId) {
                // Dropped onto empty space in another tab — move (append)
                await this.moveWidget(sourceId, tabId);
            }
        });
    }

    async _renderWidgetsForTab(tabData) {
        for (const widgetLayoutConfig of tabData.widgets) {
            if (widgetLayoutConfig.name) {
                await this.renderSingleWidget(tabData, widgetLayoutConfig);
            }
        }
    }


    async addTab(title = null, widgets = [], active = false, skipSave = false) {
        const tabId = `tab-${this.nextTabId++}`;
        const tabData = {
            id: tabId,
            title: title || `${this.t("New Tab")} ${this.nextTabId - 1}`,
            active: active || (this.tabs.length === 0),
            widgets: widgets ? widgets.slice(0) : []  // take the incoming widgets list
        };
        this.tabs.push(tabData);

        // Create tab navigation element.
        const li = document.createElement("li");
        li.className = "nav-item dashboard-tab";
        li.setAttribute("role", "presentation");
        const a = document.createElement("a");
        a.className = "nav-link";
        a.id = `${tabId}-tab`;
        a.href = `#${tabId}`;
        a.setAttribute("role", "tab");
        a.setAttribute("aria-controls", tabId);
        a.setAttribute("aria-selected", "false");
        // Use a span to hold the title
        const titleSpan = document.createElement("span");
        titleSpan.textContent = tabData.title;
        a.appendChild(titleSpan);
        // Add close button unless disabled.
        if (!this.config.disableTabButtons) {
            const closeBtn = document.createElement("button");
            closeBtn.className = "dashboard-tab-close";
            closeBtn.innerHTML = `<i class="bi bi-x-lg"></i>`;
            closeBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.removeTab(tabId);
            });
            a.appendChild(closeBtn);
        }
        a.addEventListener('click', () => this.setActiveTab(tabId));
        a.addEventListener('dblclick', () => {
            const newTitle = prompt("Enter new tab name:", tabData.title);
            if (newTitle && newTitle.trim() !== "") {
                tabData.title = newTitle;
                titleSpan.textContent = newTitle;
                this.saveDashboardConfig();
            }
        });
        li.appendChild(a);
        this.tabsContainer.appendChild(li);
        this.tabNavElements[tabId] = li;

        // Create tab content element.
        const tabContent = document.createElement("div");
        tabContent.className = "tab-pane fade";
        if (tabData.active) {
            tabContent.classList.add("show", "active");
        }
        tabContent.id = tabId;
        tabContent.setAttribute("role", "tabpanel");
        tabContent.setAttribute("aria-labelledby", `${tabId}-tab`);
        // Create a grid container within the tab content.
        const widgetHolder = document.createElement("div");
        if (this.config.resizableWidgets) {
            widgetHolder.className = "dashboard-content-resizable";
        } else {
            widgetHolder.className = "dashboard-content-auto";
        }

        this.setupDragAndDrop(widgetHolder, tabId);

        tabContent.appendChild(widgetHolder);
        this.tabContentElements[tabId] = tabContent;
        this.tabContentContainer.appendChild(tabContent);

        if (widgets && widgets.length > 0) {
            for (const widgetConfig of widgets) {
                await this.addWidgetToExistingTab(tabData, widgetConfig, true, skipSave);
            }
        }

        this.setActiveTab(tabId);
        if (!skipSave) { await this.saveDashboardConfig(); }
        return tabId;
    }

    getDropIndex(widgetHolder, dropEvent) {
        // Get drop Y coordinate relative to the widgetHolder.
        const rect = widgetHolder.getBoundingClientRect();
        const dropY = dropEvent.clientY - rect.top;
        // Iterate through widgetHolder children to determine the insertion index.
        const children = Array.from(widgetHolder.children);
        let index = 0;
        for (const child of children) {
            const childRect = child.getBoundingClientRect();
            // Calculate the vertical midpoint of the child relative to widgetHolder.
            const childCenter = (childRect.top + childRect.bottom) / 2 - rect.top;
            if (dropY < childCenter) {
                return index;
            }
            index++;
        }
        return children.length;
    }

    async removeTab(tabId) {
        const tabIndex = this.tabs.findIndex(tab => tab.id === tabId);
        if (tabIndex === -1) return;
        const tabData = this.tabs[tabIndex];
        // Destroy all widget instances in this tab.
        for (const widgetConfig of tabData.widgets) {
            const widgetInst = this.widgetInstances[widgetConfig.widgetId];
            if (widgetInst) {
                if (typeof widgetInst.destroy === 'function') { widgetInst.destroy(); }
                if (widgetInst.container && widgetInst.container.parentNode) {
                    widgetInst.container.parentNode.removeChild(widgetInst.container);
                }
                delete this.widgetInstances[widgetConfig.widgetId];
            }
        }
        // Remove tab from state.
        this.tabs.splice(tabIndex, 1);
        // Remove its DOM nodes.
        if (this.tabNavElements[tabId]) { this.tabNavElements[tabId].remove(); delete this.tabNavElements[tabId]; }
        if (this.tabContentElements[tabId]) { this.tabContentElements[tabId].remove(); delete this.tabContentElements[tabId]; }
        // Set a new active tab if needed.
        if (this.activeTabId === tabId) {
            if (this.tabs.length > 0) { this.setActiveTab(this.tabs[0].id); }
            else { this.activeTabId = null; }
        }
        await this.saveDashboardConfig();
    }

    setActiveTab(tabId) {
        this.tabs.forEach(tab => {
            const isActive = (tab.id === tabId);
            tab.active = isActive;
            if (this.tabNavElements[tab.id]) {
                const a = this.tabNavElements[tab.id].querySelector('a');
                if (isActive) { a.classList.add('active'); }
                else { a.classList.remove('active'); }
            }
            if (this.tabContentElements[tab.id]) {
                if (isActive) { this.tabContentElements[tab.id].classList.add("show", "active"); }
                else { this.tabContentElements[tab.id].classList.remove("show", "active"); }
            }
        });
        this.activeTabId = tabId;
    }

    async addWidgetToTab(tabObj, widgetConfig) {
        const tabData = this.tabs.find(tab => tab.id === tabObj.id);
        if (!tabData) return;

        const newWidgetId = widgetConfig.widgetId || `${widgetConfig.name}-${this.nextWidgetId++}`;
        const newWidgetLayoutConfig = { ...widgetConfig, widgetId: newWidgetId };
        tabData.widgets.push(newWidgetLayoutConfig);

        // We don't need to re-render the whole tab, just the new widget.
        // Let's call a single-widget render method.
        await this.renderSingleWidget(tabData, newWidgetLayoutConfig);

        // After successfully adding and rendering, save the config.
        await this.saveDashboardConfig();
    }


    async renderSingleWidget(tabData, widgetLayoutConfig) {
        const widgetHolder = this.tabContentElements[tabData.id]?.querySelector(this.getWidgetHolderClass());
        if (!widgetHolder) {
            this.log.error(`Widget holder for tab ${tabData.id} not found.`);
            return;
        }

        try {
            // 1. Load the module (Bundle or File)
            const widgetModule = await import(`/widgets/${widgetLayoutConfig.name}.js`);

            // 2. Determine the Export Name
            // In Bundled Mode: The export is named after the file (sanitized).
            // e.g. "NextActivityWidget.widget.js" -> "NextActivityWidget_widget"
            const exportName = widgetLayoutConfig.name.replace(/\./g, '_');

            // 3. Resolve the Definition
            // Check Named Export (Prod) -> Fallback to Default (Dev)
            const widgetDefinition = widgetModule[exportName] || widgetModule.default;

            if (!widgetDefinition) {
                throw new Error(`Module for ${widgetLayoutConfig.name} did not have a valid export (${exportName} or default).`);
            }

            const finalWidgetConfig = {
                ...widgetDefinition.widget,
                ...widgetLayoutConfig
            };

            const widgetInstance = await this.appClient.layoutEngine.componentManager.createManagedComponent(
                widgetDefinition.type,
                null,
                {
                    widget: finalWidgetConfig,
                    widgetId: widgetLayoutConfig.widgetId,
                    dashboard: this,
                    instanceId: widgetLayoutConfig.widgetId,
                }
            );

            if (widgetInstance) {
                this.widgetInstances[widgetLayoutConfig.widgetId] = widgetInstance;
                const widgetElement = await widgetInstance.render();
                widgetHolder.appendChild(widgetElement);

                widgetInstance.on('close', () => {
                    this.removeWidget(tabData.id, widgetLayoutConfig.widgetId);
                });
            }
        } catch (error) {
            this.log.error(`Failed to load and render widget '${widgetLayoutConfig.name}':`, error);
            const errorPlaceholder = document.createElement('div');
            errorPlaceholder.className = 'widget-container-auto';
            errorPlaceholder.innerHTML = `<div class="widget-title-bar">Error</div><div class="widget-content p-3 text-danger">Failed to load widget: ${widgetLayoutConfig.name}</div>`;
            widgetHolder.appendChild(errorPlaceholder);
        }
    }


    getWidgetHolderClass() {
        return this.config.resizableWidgets ? ".dashboard-content-resizable" : ".dashboard-content-auto";
    }

    async addWidgetToExistingTab(tabData, widgetConfig, skipPush = false, skipSave = false) {
        const tabContent = this.tabContentElements[tabData.id];
        if (!tabContent) return;

        const widgetHolder = tabContent.querySelector(this.getWidgetHolderClass());

        // --- NEW LOGIC: Find the full widget definition from the available list ---
        const availableWidgetDef = this.availableWidgets.find(w => w.name === widgetConfig.name);
        if (!availableWidgetDef) {
            this.log.warn(`Widget '${widgetConfig.name}' from saved config not available. Skipping render.`);
            return; // Skip rendering this widget if its definition isn't found
        }

        // Merge the full config from the definition with the saved layout config
        const finalWidgetConfig = { ...availableWidgetDef.config, ...widgetConfig };
        // --- END NEW LOGIC ---

        const widgetInstance = await this.appClient.layoutEngine.componentManager.createManagedComponent(
            availableWidgetDef.type, // Use the correct type from the definition
            null,
            {
                // This 'props' wrapper is no longer necessary with the new ComponentManager
                widget: finalWidgetConfig,
                widgetId: widgetConfig.widgetId,
                dashboard: this,
                instanceId: widgetConfig.widgetId,
            }
        );

        if (widgetInstance) {
            this.widgetInstances[widgetConfig.widgetId] = widgetInstance;
            const widgetElement = await widgetInstance.render();
            widgetHolder.appendChild(widgetElement);

            widgetInstance.on('close', () => {
                this.removeWidget(tabData.id, widgetInstance.widgetId);
            });

            if (!skipPush) {
                tabData.widgets.push(widgetConfig);
            }
        }

        if (!skipSave) {
            await this.saveDashboardConfig();
        }
    }


    async removeWidget(tabId, widgetId) {
        const tabData = this.tabs.find(tab => tab.id === tabId);
        if (!tabData) return;

        const widgetIndex = tabData.widgets.findIndex(w => w.widgetId === widgetId);
        if (widgetIndex === -1) return;

        tabData.widgets.splice(widgetIndex, 1);

        const widgetInst = this.widgetInstances[widgetId];
        if (widgetInst) {
            widgetInst.destroy?.();
            widgetInst.container?.remove();
            delete this.widgetInstances[widgetId];
        }

        await this.saveDashboardConfig();
        this.log.info(`Removed widget ${widgetId} from tab ${tabId} and saved configuration.`);
    }


    // Swap two widgets' positions (both in data and DOM).
    async swapWidgets(sourceId, targetId) {
        // Locate both widgets across all tabs
        let sourceTab = null, sourceIndex = -1;
        let targetTab = null, targetIndex = -1;

        for (const tab of this.tabs) {
            if (sourceIndex === -1) {
                const idx = tab.widgets.findIndex(w => w.widgetId === sourceId);
                if (idx > -1) { sourceTab = tab; sourceIndex = idx; }
            }
            if (targetIndex === -1) {
                const idx = tab.widgets.findIndex(w => w.widgetId === targetId);
                if (idx > -1) { targetTab = tab; targetIndex = idx; }
            }
            if (sourceTab && targetTab) break;
        }

        if (!sourceTab || !targetTab || sourceIndex === -1 || targetIndex === -1) return;

        // Swap data array entries
        const sourceCfg = sourceTab.widgets[sourceIndex];
        const targetCfg = targetTab.widgets[targetIndex];
        sourceTab.widgets[sourceIndex] = targetCfg;
        targetTab.widgets[targetIndex] = sourceCfg;

        // Swap DOM elements using a comment-node placeholder to avoid losing event listeners
        const sourceContainer = this.widgetInstances[sourceId]?.container;
        const targetContainer = this.widgetInstances[targetId]?.container;

        if (sourceContainer && targetContainer) {
            const tmp = document.createComment('swap-placeholder');
            sourceContainer.parentNode.insertBefore(tmp, sourceContainer);
            targetContainer.parentNode.insertBefore(sourceContainer, targetContainer);
            tmp.parentNode.insertBefore(targetContainer, tmp);
            tmp.remove();
        }

        await this.saveDashboardConfig();
        this.log.info(`Swapped widgets: ${sourceId} ↔ ${targetId}`);
    }


    syncWidgetDimensions(widgetId, cols, rows) {
        for (const tab of this.tabs) {
            const widget = tab.widgets.find(w => w.widgetId === widgetId);
            if (widget) {
                widget.cols = cols;
                widget.rows = rows;
                break;
            }
        }
    }


    // --- DateRange Picker helpers (same as before) ---
    createDateRangePicker() {
        const dropdownDiv = document.createElement("div");
        dropdownDiv.className = "dropdown";
        const button = document.createElement("button");
        button.className = "btn btn-outline-secondary btn-sm dropdown-toggle";
        button.type = "button";
        button.id = "daterangePicker";
        button.setAttribute("data-bs-toggle", "dropdown");
        button.setAttribute("aria-expanded", "false");
        button.textContent = this.currentDateRange.label;
        button.setAttribute("data-i18n", this.currentDateRange.label);
        dropdownDiv.appendChild(button);
        const menu = document.createElement("ul");
        menu.className = "dropdown-menu";
        menu.setAttribute("aria-labelledby", "daterangePicker");
        const ranges = [
            { label: "Today", calc: () => { let today = new Date(); return { startDate: today, endDate: today }; } },
            { label: "Yesterday", calc: () => { let d = new Date(); d.setDate(d.getDate() - 1); return { startDate: d, endDate: d }; } },
            {
                label: "This Week", calc: () => {
                    let d = new Date(), day = d.getDay(), start = new Date(d);
                    start.setDate(d.getDate() - day + 1);
                    return { startDate: start, endDate: d };
                }
            },
            {
                label: "Last Week", calc: () => {
                    let d = new Date(), day = d.getDay(), start = new Date(d);
                    start.setDate(d.getDate() - day - 6);
                    let end = new Date(d);
                    end.setDate(d.getDate() - day);
                    return { startDate: start, endDate: end };
                }
            },
            {
                label: "This Month", calc: () => {
                    let d = new Date(), start = new Date(d.getFullYear(), d.getMonth(), 1);
                    return { startDate: start, endDate: d };
                }
            },
            {
                label: "Last Month", calc: () => {
                    let d = new Date(), start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
                    let end = new Date(d.getFullYear(), d.getMonth(), 0);
                    return { startDate: start, endDate: end };
                }
            },
            {
                label: "This Year", calc: () => {
                    let d = new Date(), start = new Date(d.getFullYear(), 0, 1);
                    return { startDate: start, endDate: d };
                }
            },
            {
                label: "Last Year", calc: () => {
                    let d = new Date(), start = new Date(d.getFullYear() - 1, 0, 1);
                    let end = new Date(d.getFullYear() - 1, 11, 31);
                    return { startDate: start, endDate: end };
                }
            },
            { label: "Custom", calc: null }
        ];
        ranges.forEach(r => {
            const li = document.createElement("li");
            const a = document.createElement("a");
            a.className = "dropdown-item";
            a.href = "#";
            a.textContent = r.label;
            a.setAttribute("data-i18n", r.label);
            a.addEventListener('click', async (e) => {
                e.preventDefault();
                if (r.calc) {
                    const range = r.calc();
                    range.label = this.t(r.label);
                    this.setDateRange(range);
                } else {
                    await this.showCustomDateRangeModal();
                }
            });
            li.appendChild(a);
            menu.appendChild(li);
        });
        dropdownDiv.appendChild(menu);
        return dropdownDiv;
    }

    setDateRange(range) {
        this.currentDateRange = range;
        localStorage.setItem(`dashboard-${this.config.instanceId}-daterange`, JSON.stringify(range));
        const btn = document.getElementById("daterangePicker");
        if (btn) { btn.textContent = this.t(range.label); }
        const eventName = `dashboard-${this.config.instanceId}-daterange-changed`;
        this.appClient.events.fireLocal(eventName, range);
    }

    loadSavedDateRange() {
        const stored = localStorage.getItem(`dashboard-${this.config.instanceId}-daterange`);
        if (stored) {
            try { return JSON.parse(stored); }
            catch (e) { return null; }
        }
        return null;
    }

    getDefaultDateRange() {
        let today = new Date();
        return { label: "Today", startDate: today, endDate: today };
    }

    async showCustomDateRangeModal() {
        const modal = document.createElement("div");
        modal.className = "modal fade";
        modal.id = "customDateModal";
        modal.setAttribute("tabindex", "-1");
        modal.setAttribute("aria-labelledby", "customDateModalLabel");
        modal.setAttribute("aria-hidden", "true");
        const modalDialog = document.createElement("div");
        modalDialog.className = "modal-dialog";
        modal.appendChild(modalDialog);
        const modalContent = document.createElement("div");
        modalContent.className = "modal-content";
        modalDialog.appendChild(modalContent);
        modalContent.innerHTML = `
      <div class="modal-header">
        <h5 class="modal-title" id="customDateModalLabel">Select Custom Date Range</h5>
        <button type="button" class="btn-close" title="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <div class="mb-3">
          <label for="customStart" class="form-label">Start Date:</label>
          <input type="date" id="customStart" class="form-control">
        </div>
        <div class="mb-3">
          <label for="customEnd" class="form-label">End Date:</label>
          <input type="date" id="customEnd" class="form-control">
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button id="applyCustomDate" type="button" class="btn btn-primary">Apply</button>
      </div>
    `;
        document.body.appendChild(modal);
        const bsModal = new bootstrap.Modal(modal);
        bsModal.show();
        modal.querySelector("#applyCustomDate").addEventListener("click", () => {
            const start = modal.querySelector("#customStart").value;
            const end = modal.querySelector("#customEnd").value;
            if (start && end) {
                this.setDateRange({ label: "Custom", startDate: new Date(start), endDate: new Date(end) });
            }
            bsModal.hide();
        });
        modal.addEventListener('hidden.bs.modal', () => {
            modal.remove();
        });
    }

    async saveDashboardConfig() {
        if (this.config.disableCustomizations) {
            this.log.debug("Dashboard customizations disabled, skipping save.");
            return;
        }

        const configToSave = {
            tabs: this.tabs.map(tab => ({
                id: tab.id,
                title: tab.title,
                active: tab.active,
                widgets: tab.widgets.map(widget => ({
                    name: widget.name,
                    widgetId: widget.widgetId,
                    rows: widget.rows,
                    cols: widget.cols,
                })),
            })),
            activeTabId: this.activeTabId,
            nextWidgetId: this.nextWidgetId,
            nextTabId: this.nextTabId,
            currentDateRange: this.currentDateRange,
            gridCellSize: 10,
            gridGap: 10
        };
        try {
            await this.appClient.rpcClient.call(
                'DashboardRpc.saveConfig',
                { config: configToSave, instanceId: this.config.instanceId },
                { userId: this.appClient.getSharedData("userId") }
            );
            this.log.info(`Dashboard configuration saved for instance: ${this.config.instanceId}`);
        } catch (error) {
            this.log.error('Error saving dashboard configuration:', error);
        }
    }

    async loadDashboardConfig() {
        try {
            const config = await this.appClient.rpcClient.call(
                'DashboardRpc.loadConfig',
                { instanceId: this.config.instanceId },
                { userId: this.appClient.getSharedData("userId") }
            );

            if (config && config.tabs && config.tabs.length > 0) {
                if (config.gridCellSize !== 10) {
                    config.tabs.forEach(tab => {
                        if (tab.widgets) {
                            tab.widgets = this.migrateWidgets(tab.widgets);
                        }
                    });
                    this.tabs = config.tabs;
                    this.activeTabId = config.activeTabId;
                    this.nextTabId = config.nextTabId || (this.tabs.length + 1);
                    this.nextWidgetId = config.nextWidgetId || 100;
                    await this.saveDashboardConfig();
                } else {
                    this.tabs = config.tabs;
                    this.activeTabId = config.activeTabId;
                    this.nextTabId = config.nextTabId || (this.tabs.length + 1);
                    this.nextWidgetId = config.nextWidgetId || 100;
                }
                // Re-render the entire dashboard from the loaded state
                await this.reRenderAll();
            } else {
                // If no saved config, create a default tab.
                await this.addTab('Dashboard', [], true, true);
            }
            this.log.info(`Dashboard configuration loaded for instance: ${this.config.instanceId}`);
        } catch (error) {
            this.log.error(`Error loading dashboard configuration for instance: ${this.config.instanceId}`, error);
            // On error, create a clean default tab to prevent a blank screen.
            await this.addTab('Default Tab', [], true, true);
        }
    }


    async showWidgetModal() {
        const modalId = `${this.config.instanceId}-widgetModal`;
        document.getElementById(modalId)?.remove(); // Clean up previous modal if it exists

        const modal = document.createElement("div");
        modal.className = "modal fade";
        modal.id = modalId;
        modal.setAttribute("tabindex", "-1");
        modal.innerHTML = `
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">${this.t('Add Widget')}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="list-group"></div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${this.t('Close')}</button>
                        <button type="button" class="btn btn-primary" id="add-selected-widget" disabled>${this.t('Add Widget')}</button>
                    </div>
                </div>
            </div>
        `;

        const widgetListContainer = modal.querySelector('.list-group');
        const addButton = modal.querySelector('#add-selected-widget');

        let widgetsToList = this.availableWidgets;
        if (this.config.role) {
            widgetsToList = this.availableWidgets.filter(widget =>
                widget.roles && widget.roles.includes(this.config.role)
            );
        }

        widgetsToList.forEach(widget => {
            const listItem = document.createElement("button");
            listItem.type = "button";
            listItem.className = "list-group-item list-group-item-action";
            listItem.innerHTML = `<strong>${this.t(widget.name)}</strong><br><small>${this.t(widget.description)}</small>`;
            listItem.dataset.widgetName = widget.name; // Store the name for retrieval

            listItem.addEventListener('click', () => {
                widgetListContainer.querySelectorAll('.list-group-item').forEach(item => item.classList.remove('active'));
                listItem.classList.add('active');
                addButton.disabled = false;
            });
            widgetListContainer.appendChild(listItem);
        });

        // --- THIS IS THE RESTORED LOGIC ---
        addButton.addEventListener('click', async () => {
            const activeListItem = widgetListContainer.querySelector('.list-group-item.active');
            if (activeListItem) {
                const widgetName = activeListItem.dataset.widgetName;
                const activeTab = this.tabs.find(tab => tab.active);

                if (activeTab && widgetName) {
                    const widgetConfig = { name: widgetName }; // The minimum needed to identify the widget
                    await this.addWidgetToTab(activeTab, widgetConfig);
                }
                bootstrap.Modal.getInstance(modal).hide();
            }
        });
        // --- END RESTORED LOGIC ---

        document.body.appendChild(modal);
        const bsModal = new bootstrap.Modal(modal);
        bsModal.show();
        modal.addEventListener('hidden.bs.modal', () => modal.remove(), { once: true });
    }


    async reloadWidget(event) {
        this.log.info(`Reloading widget: ${event.name}`, event);

        // Iterate through all tabs and their widgets
        for (const tab of this.tabs) {
            for (const widgetConfig of tab.widgets) {
                if (widgetConfig.name === event.name) {
                    // Find the widget instance and its container
                    const widgetInstance = this.widgetInstances[widgetConfig.widgetId];
                    const widgetContainer = widgetInstance?.container;

                    if (widgetInstance && widgetContainer) {
                        // Destroy the old instance
                        if (typeof widgetInstance.destroy === 'function') {
                            await widgetInstance.destroy();
                        }

                        const parent = widgetContainer.parentNode;
                        const nextSibling = widgetContainer.nextSibling;
                        widgetContainer.remove();
                        delete this.widgetInstances[widgetConfig.widgetId];


                        // Create and render the new widget instance
                        const availableWidget = this.availableWidgets.find(w => w.name === widgetConfig.name);
                        if (!availableWidget) {
                            console.warn(`Widget ${widgetConfig.name} no longer available.`);
                            return;
                        }
                        const newWidgetInstance = await this.appClient.layoutEngine.componentManager.createManagedComponent(
                            availableWidget.type,
                            null,
                            {
                                props: {
                                    widget: event.config.widget,
                                    widgetId: widgetConfig.widgetId,
                                    title: availableWidget.config.title,
                                },
                                instanceId: widgetConfig.widgetId,
                            }, false);



                        if (newWidgetInstance) {
                            newWidgetInstance.dashboard = this;

                            this.widgetInstances[widgetConfig.widgetId] = newWidgetInstance;
                            newWidgetInstance.widgetId = widgetConfig.widgetId;
                            const widgetElement = await newWidgetInstance.render();

                            // Find the tab content and widget holder for the current tab
                            const tabContent = this.tabContentElements[tab.id];
                            if (tabContent) {
                                const widgetHolder = tabContent.querySelector(this.getWidgetHolderClass());
                                if (parent) {
                                    if (nextSibling) {
                                        parent.insertBefore(widgetElement, nextSibling);
                                    } else {
                                        parent.appendChild(widgetElement);
                                    }
                                }
                            }

                            newWidgetInstance.on('close', () => {
                                this.removeWidget(tab.id, newWidgetInstance.widgetId);
                            });
                        }
                    }
                }
            }
        }
    }

    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }


    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        for (const widgetId in this.widgetInstances) {
            if (this.widgetInstances[widgetId]?.destroy) {
                this.widgetInstances[widgetId].destroy();
            }
        }
        this.widgetInstances = {};
        super.destroy();
    }
}
